import { apiFetch } from '@/lib/api';

// ─── Shared HTTP Helpers ────────────────────────────────────────────────────

/**
 * Error class that preserves structured server error metadata
 * (`code`, `details`, HTTP `status`). The plain `Error` previously thrown by
 * these helpers discarded everything except `message`, which silently broke
 * downstream handlers that branch on `err?.code` (e.g. WorkItemDrawer,
 * WorkItemBoard, WorkItemFormDialog, VerificationInboxPage). RR-0004 rework:
 * keep the structured payload so callers can react to specific codes such
 * as `ROROS_WIP_LIMIT_REACHED`.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, ApiError.prototype);
  }
}

async function throwApiError(res: Response): Promise<never> {
  const errorBody = await res.json().catch(() => null);
  const code = errorBody?.error?.code || 'UNKNOWN';
  const message = errorBody?.error?.message || `Request failed (${res.status})`;
  const details = errorBody?.error?.details;
  throw new ApiError(res.status, code, message, details);
}

/**
 * Fetch a JSON resource with auth, unwrapping the API response envelope.
 *
 * The ROROS API always returns `{ success: true, data: T }`. This helper:
 *   1. Calls `apiFetch` (adds auth headers automatically).
 *   2. Throws `ApiError` (with `.status`, `.code`, `.details`) on any non-2xx response.
 *   3. Unwraps the envelope and returns `data` directly as `T`.
 *
 * CORRECT usage — the return value is already the unwrapped data:
 * ```ts
 * const items = await fetchJson<MyType[]>('/api/v1/roros/items');
 * // items is MyType[], NOT { success, data }
 * ```
 *
 * DO NOT double-unwrap. The following is WRONG and has caused bugs 3+ times:
 * ```ts
 * const res = await fetchJson<{ data: MyType[] }>('/api/v1/roros/items');
 * const items = res.data; // BUG — res is already MyType[], .data is undefined
 * ```
 *
 * Envelope shape for reference: `{ success: true, data: T }`
 */
export async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(url, init);
  if (!res.ok) await throwApiError(res);
  const json = await res.json();
  return json.data as T;
}

export async function mutate<T = void>(url: string, method: string, body?: unknown): Promise<T> {
  const res = await apiFetch(url, {
    method,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!res.ok) await throwApiError(res);
  const json = await res.json().catch(() => ({ data: undefined }));
  return json.data as T;
}

export function qs(params?: Record<string, string>): string {
  return params ? `?${new URLSearchParams(params)}` : '';
}
